import { Link, NavLink, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { KeepAwake } from '@capacitor-community/keep-awake';
import { useMe, useExamSectionLookup, useEnrollments, useContextXp, useGeneralExams, useActiveAnnouncements, useDismissAnnouncement, useSubmitBugReport, recordPresence, useUnreadNotificationCount, useNotifications, useMarkNotificationRead, useMarkAllNotificationsRead, useLoginRewardStatus, useClaimLoginReward } from '../lib/api';
import { useXp } from '../lib/xp-context';
import { useIngestion } from '../lib/ingestion-context';
import { setAppScrollEl } from '../lib/scroll';
import { initPushNotifications } from '../lib/push';

export function AppShell() {
  const { data } = useMe();
  const xp = useXp();
  const location = useLocation();
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);

  // Register for native push once the user is authenticated; deep-link on tap.
  useEffect(() => {
    if (!data?.user?.id) return;
    initPushNotifications((route) => navigate(route));
  }, [data?.user?.id]);

  // Register the scroll container so scrollAppToTop() can drive it, and reset
  // scroll position to the top whenever the route changes (the container
  // persists across routes, so it would otherwise keep the previous scroll).
  useEffect(() => { setAppScrollEl(scrollRef.current); }, []);
  useEffect(() => { scrollRef.current?.scrollTo({ top: 0 }); }, [location.pathname]);

  // Sync global XP from /me on load
  useEffect(() => {
    if (data?.stats.totalXp != null) xp.setTotalXp(data.stats.totalXp);
  }, [data?.stats.totalXp]);

  // Determine context from URL
  const ctx = resolveContext(location.pathname);

  useKeepScreenAwake();
  usePresenceHeartbeat(location.pathname, ctx);

  // Fetch context info
  const { data: sectionLookup } = useExamSectionLookup(ctx.type === 'exam_section' ? ctx.id : null);
  const { data: examsData } = useGeneralExams();
  const { data: enrollData } = useEnrollments();

  // For exam dashboard, look up exam directly from exams list
  const examFromList = ctx.type === 'exam' ? examsData?.exams?.find(e => e.id === ctx.id) : null;

  // Context XP — for exam_section use that section; for class use that section; for exam dashboard skip (no single section)
  const contextId = ctx.type === 'exam_section' ? ctx.id
    : ctx.type === 'class' ? ctx.id
    : null;
  const { data: ctxXpData } = useContextXp(contextId);

  // Resolve context label and sync to XP context
  useEffect(() => {
    let label = '';
    let id: string | null = null;

    if (ctx.type === 'exam_section' && sectionLookup?.exam) {
      label = sectionLookup.exam.code;
      id = ctx.id;
    } else if (ctx.type === 'exam' && examFromList) {
      label = examFromList.code;
      id = null; // no single section for the dashboard
    } else if (ctx.type === 'class') {
      const enrollment = enrollData?.enrollments?.find((e: any) => e.id === ctx.id);
      if (enrollment) {
        label = enrollment.class_code;
        id = ctx.id;
      }
    } else if (ctx.type === 'none') {
      xp.setContext('', null, 0);
      return;
    }

    if (label) {
      xp.setContext(label, id, ctxXpData?.xp ?? 0);
    }
  }, [ctx.type, ctx.id, sectionLookup, examFromList, enrollData, ctxXpData]);

  // Sync fetched context XP
  useEffect(() => {
    if (ctxXpData?.xp != null) xp.setContextXp(ctxXpData.xp);
  }, [ctxXpData?.xp]);

  const streak = data?.stats.currentStreak ?? 0;

  return (
    <div className="app-shell flex flex-col bg-slate-50 overflow-hidden">
      <header className="bg-white border-b z-20 safe-area-pt shrink-0">
        <div className="max-w-6xl mx-auto px-4 py-2.5 flex items-center justify-between">
          <Link to="/" className="shrink-0">
            <img src="/logo.png" alt="Squishy School" className="w-11 h-11" />
          </Link>

          <div className="flex items-center gap-2">
            <BugReportButton />
            <NotificationBell />
            {streak > 0 && <StreakBadge streak={streak} />}
            {xp.contextLabel && (
              <XpPill label={xp.contextLabel} xp={xp.contextXp} delta={xp.delta} variant="context" />
            )}
            <XpPillWithReward label="XP" xp={xp.totalXp} delta={xp.delta} />
          </div>

        </div>
      </header>

      <AnnouncementPopup />
      <CompletionToast />
      <IngestionBanner />
      <StudyGroupInvitePopup />

      <div ref={scrollRef} className="flex-1 overflow-y-auto">
        <main className="max-w-3xl mx-auto w-full px-4 py-4 md:py-6 has-[.study-wide]:max-w-none has-[.study-wide]:px-0">
          <Outlet />
        </main>
      </div>

      <nav className="bg-white border-t z-20 safe-area-pb shrink-0">
        <div className="grid grid-cols-7 max-w-md mx-auto">
          <TabLink to="/" label="Home" end icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0a1 1 0 01-1-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 01-1 1h-2z" />
            </svg>
          } />
          <TabLink to="/exams" label="Exams" icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
          } />
          <TabLink to="/learn" label="Learn" icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          } />
          <AddButton />
          <TabLink to="/study-groups" label="Groups" icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          } />
          <TabLink to="/leaderboard/friends" label="Friends" icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          } />
          <TabLink to="/profile" label="Me" icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          } />
        </div>
      </nav>
    </div>
  );
}

function useKeepScreenAwake() {
  useEffect(() => {
    // Native (iOS/Android): use Capacitor plugin
    if (Capacitor.isNativePlatform()) {
      KeepAwake.keepAwake().catch(() => {});
      return () => { KeepAwake.allowSleep().catch(() => {}); };
    }

    // Web/PWA: use Screen Wake Lock API
    if (!('wakeLock' in navigator)) return;

    let lock: WakeLockSentinel | null = null;

    const requestLock = async () => {
      try {
        lock = await navigator.wakeLock.request('screen');
        lock.addEventListener('release', () => { lock = null; });
      } catch {}
    };

    requestLock();

    // Re-acquire on tab visibility change (browser releases lock when tab is hidden)
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && !lock) requestLock();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      lock?.release().catch(() => {});
    };
  }, []);
}

function usePresenceHeartbeat(pathname: string, ctx: ReturnType<typeof resolveContext>) {
  useEffect(() => {
    let cancelled = false;
    let lastSent = 0;

    const send = async (force = false) => {
      if (cancelled) return;
      const visible = document.visibilityState === 'visible';
      const now = Date.now();
      if (!force && (!visible || now - lastSent < 20_000)) return;
      lastSent = now;
      await recordPresence({
        route: pathname,
        platform: Capacitor.getPlatform(),
        visibilityState: visible ? 'visible' : 'hidden',
        contextType: ctx.type,
        contextId: ctx.id || null,
      }).catch(() => {});
    };

    send(true);
    const interval = window.setInterval(() => send(false), 30_000);
    const onVisibility = () => send(true);
    window.addEventListener('focus', onVisibility);
    window.addEventListener('online', onVisibility);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.removeEventListener('focus', onVisibility);
      window.removeEventListener('online', onVisibility);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [pathname, ctx.type, ctx.id]);
}

// ── URL context resolver ────────────────────────────────────────────────────

function resolveContext(pathname: string): { type: 'exam_section' | 'exam' | 'class' | 'none'; id: string } {
  // /study/exam/:sectionId
  const studyExam = pathname.match(/^\/study\/exam\/([^/]+)/);
  if (studyExam) return { type: 'exam_section', id: studyExam[1]! };

  const examDash = pathname.match(/^\/exam\/([^/]+)/);
  if (examDash) return { type: 'exam', id: examDash[1]! };

  const studyClass = pathname.match(/^\/study\/class\/([^/]+)/);
  if (studyClass) return { type: 'class', id: studyClass[1]! };

  const classDash = pathname.match(/^\/class\/([^/]+)/);
  if (classDash) return { type: 'class', id: classDash[1]! };

  return { type: 'none', id: '' };
}

// ── Streak Badge ─────────────────────────────────────────────────────────────

function StreakBadge({ streak }: { streak: number }) {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setShowTooltip(prev => !prev)}
        className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-teal-100 text-teal-700 text-[10px] font-semibold active:scale-95 transition-transform"
      >
        <svg className="w-3 h-3 inline" viewBox="0 0 24 24" fill="currentColor"><path d="M12 23c-1.5 0-5-1.5-5-7 0-3 1.5-5 3-7-.5 2 1 3 2 3 1.5 0 2-1 2-2.5S13 6 12 4c2 0 4 1 5 3s1.5 4 1.5 6c0 5.5-3.5 10-6.5 10z"/></svg>{streak}
      </button>

      {showTooltip && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setShowTooltip(false)} />
          <div
            className="absolute top-full right-0 mt-2 z-40 w-56 bg-white rounded-xl shadow-lg border p-4 text-center"
            style={{ animation: 'streakPopIn 0.25s ease-out' }}
          >
            <div className="mb-1"><svg className="w-8 h-8 inline text-teal-500" viewBox="0 0 24 24" fill="currentColor"><path d="M12 23c-1.5 0-5-1.5-5-7 0-3 1.5-5 3-7-.5 2 1 3 2 3 1.5 0 2-1 2-2.5S13 6 12 4c2 0 4 1 5 3s1.5 4 1.5 6c0 5.5-3.5 10-6.5 10z"/></svg></div>
            <div className="text-lg font-bold text-teal-600">{streak} Day Streak!</div>
            <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">
              You've studied {streak} day{streak !== 1 ? 's' : ''} in a row. Keep it up to build your streak!
            </p>
          </div>
        </>
      )}
    </div>
  );
}

// ── XP Pill ─────────────────────────────────────────────────────────────────

function XpPill({ label, xp, delta, variant }: {
  label: string;
  xp: number;
  delta: { amount: number; key: number } | null;
  variant: 'global' | 'context';
}) {
  const [displayXp, setDisplayXp] = useState(xp);
  const [animating, setAnimating] = useState(false);
  const [floatingDelta, setFloatingDelta] = useState<{ amount: number; key: number } | null>(null);
  const prevXp = useRef(xp);

  useEffect(() => {
    if (xp === prevXp.current) { setDisplayXp(xp); return; }
    const from = prevXp.current;
    const to = xp;
    prevXp.current = xp;
    const duration = 600;
    const start = performance.now();
    setAnimating(true);
    const animate = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayXp(Math.round(from + (to - from) * eased));
      if (progress < 1) requestAnimationFrame(animate);
      else setAnimating(false);
    };
    requestAnimationFrame(animate);
  }, [xp]);

  useEffect(() => {
    if (!delta || delta.amount === 0) return;
    setFloatingDelta(delta);
    const timer = setTimeout(() => setFloatingDelta(null), 1200);
    return () => clearTimeout(timer);
  }, [delta?.key]);

  const bgClass = variant === 'global'
    ? 'bg-squishy-teal text-squishy-navy'
    : 'bg-indigo-100 text-indigo-700';

  return (
    <div className="relative">
      <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${bgClass} ${animating ? 'scale-110' : 'scale-100'} transition-transform duration-200`}>
        <span className="opacity-80">{label}</span>
        <span>{displayXp.toLocaleString()}</span>
      </div>

      {floatingDelta && (
        <span
          key={floatingDelta.key}
          className={`absolute -top-1 left-1/2 -translate-x-1/2 text-sm font-black pointer-events-none whitespace-nowrap ${
            floatingDelta.amount > 0 ? 'text-emerald-500' : 'text-red-500'
          }`}
          style={{ animation: 'xpBurst 1.2s ease-out forwards' }}
        >
          {floatingDelta.amount > 0 ? '+' : ''}{floatingDelta.amount}
        </span>
      )}

      {floatingDelta && floatingDelta.amount > 0 && (
        <div className="absolute top-0 left-1/2 -translate-x-1/2 pointer-events-none">
          {[...Array(6)].map((_, i) => (
            <span
              key={`${floatingDelta.key}-p${i}`}
              className="absolute w-1.5 h-1.5 rounded-full bg-emerald-400"
              style={{
                animation: 'xpParticle 0.8s ease-out forwards',
                animationDelay: `${i * 40}ms`,
                ['--angle' as any]: `${i * 60}deg`,
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── XP Pill with Daily Reward Dropdown ────────────────────────────────────────

function XpPillWithReward({ label, xp, delta }: {
  label: string;
  xp: number;
  delta: { amount: number; key: number } | null;
}) {
  const [open, setOpen] = useState(false);
  const { data: rewardData } = useLoginRewardStatus();
  const claim = useClaimLoginReward();
  const xpCtx = useXp();

  const handleClaim = async () => {
    try {
      const result = await claim.mutateAsync();
      if (result.xpAwarded) xpCtx.pushDelta(result.xpAwarded);
    } catch { /* already claimed or error */ }
  };

  const hasUnclaimedReward = rewardData && !rewardData.claimedToday;

  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)} className="relative">
        <XpPill label={label} xp={xp} delta={delta} variant="global" />
        {hasUnclaimedReward && (
          <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-teal-400 rounded-full border-2 border-white animate-pulse" />
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div
            className="absolute right-0 top-full mt-2 z-40 w-72 bg-white rounded-xl shadow-lg border overflow-hidden"
            style={{ animation: 'streakPopIn 0.25s ease-out' }}
          >
            {rewardData ? (
              <div className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-squishy-navy">Daily Reward</h3>
                  {rewardData.proMultiplier && (
                    <span className="text-[10px] bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded-full border border-indigo-200 font-medium">2x Pro</span>
                  )}
                </div>
                <div className="flex gap-1.5">
                  {rewardData.rewards.map((r, i) => {
                    const day = i + 1;
                    const isCurrent = day === rewardData.currentDay;
                    const isClaimed = rewardData.claimedToday ? day <= rewardData.currentDay : day < rewardData.currentDay;

                    return (
                      <button
                        key={day}
                        onClick={isCurrent && !rewardData.claimedToday ? handleClaim : undefined}
                        disabled={!isCurrent || rewardData.claimedToday || claim.isPending}
                        className={`flex-1 rounded-xl py-2 text-center text-xs transition-all ${
                          isClaimed
                            ? 'bg-emerald-50 border border-emerald-200 text-emerald-600'
                            : isCurrent && !rewardData.claimedToday
                              ? 'bg-teal-50 border-2 border-teal-300 text-teal-700 hover:bg-teal-100 cursor-pointer active:scale-95'
                              : 'bg-slate-50 border border-slate-100 text-slate-400'
                        }`}
                      >
                        <div className="font-bold text-[11px]">{isClaimed ? '\u2713' : `+${rewardData.proMultiplier ? r * 2 : r}`}</div>
                        <div className="text-[9px] mt-0.5 opacity-70">Day {day}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="p-4 text-center text-sm text-slate-400">Loading...</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── Announcement Popup ───────────────────────────────────────────────────────

// Pops a modal when an unread study-group invite arrives, sending the friend
// to Groups to accept. Notifications poll every 25s (see useNotifications).
function StudyGroupInvitePopup() {
  const navigate = useNavigate();
  const location = useLocation();
  const { data } = useNotifications(10);
  const [dismissed, setDismissed] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem('sg-invite-seen') || '[]')); } catch { return new Set(); }
  });

  // Don't interrupt while they're already inside Study Groups.
  if (location.pathname.startsWith('/study-groups') || location.pathname.startsWith('/groups')) return null;

  const invite = (data?.notifications ?? []).find(
    (n: any) => n.type === 'study_group_invite' && !n.read && !dismissed.has(n.id),
  );
  if (!invite) return null;

  const dismiss = () => {
    setDismissed((prev) => {
      const next = new Set(prev);
      next.add(invite.id);
      try { localStorage.setItem('sg-invite-seen', JSON.stringify([...next].slice(-50))); } catch { /* ignore */ }
      return next;
    });
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center px-4 animate-fade-in">
      <div className="absolute inset-0 bg-black/40" onClick={dismiss} />
      <div className="relative bg-white w-full max-w-sm rounded-2xl shadow-xl p-5 animate-slide-up text-center space-y-3">
        <div className="text-4xl">&#x1F3AE;</div>
        <h2 className="text-lg font-bold text-squishy-dark">{invite.title || 'Study group invite'}</h2>
        <p className="text-sm text-slate-600">{invite.body || 'A friend invited you to a study group.'}</p>
        <div className="flex gap-2 pt-1">
          <button onClick={dismiss} className="flex-1 py-2.5 rounded-xl border-2 border-slate-200 text-sm font-semibold text-slate-600 active:scale-[0.97] transition">Later</button>
          <button
            onClick={() => { dismiss(); navigate('/study-groups'); }}
            className="flex-1 py-2.5 rounded-xl bg-squishy-primary text-white text-sm font-semibold active:scale-[0.97] transition"
          >
            Go to Groups
          </button>
        </div>
      </div>
    </div>
  );
}

function AnnouncementPopup() {
  const { data } = useActiveAnnouncements();
  const dismiss = useDismissAnnouncement();
  const announcement = data?.announcements?.[0];

  if (!announcement) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={() => dismiss.mutate(announcement.id)} />
      <div className="relative bg-white rounded-2xl max-w-md w-full shadow-xl animate-slide-up overflow-hidden">
        <div className="bg-gradient-to-r from-squishy-tealDark to-indigo-900 px-5 py-4">
          <h2 className="text-white font-bold text-lg">{announcement.title}</h2>
        </div>
        <div className="px-5 py-4">
          <p className="text-slate-700 text-sm whitespace-pre-wrap">{announcement.body}</p>
        </div>
        <div className="px-5 pb-4">
          <button
            onClick={() => dismiss.mutate(announcement.id)}
            className="w-full bg-squishy-tealDark text-white py-2.5 rounded-xl font-semibold text-sm hover:opacity-90 transition"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}

// Inject keyframes
const styleId = 'xp-animations';
if (typeof document !== 'undefined' && !document.getElementById(styleId)) {
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    @keyframes streakPopIn {
      0% { opacity: 0; transform: scale(0.8) translateY(-4px); }
      50% { transform: scale(1.05) translateY(0); }
      100% { opacity: 1; transform: scale(1) translateY(0); }
    }
    @keyframes xpBurst {
      0% { opacity: 1; transform: translate(-50%, 0) scale(1); }
      30% { opacity: 1; transform: translate(-50%, -18px) scale(1.3); }
      100% { opacity: 0; transform: translate(-50%, -36px) scale(0.8); }
    }
    @keyframes xpParticle {
      0% { opacity: 1; transform: translate(0, 0) scale(1); }
      100% { opacity: 0; transform: translate(
        calc(cos(var(--angle)) * 20px),
        calc(sin(var(--angle)) * -20px)
      ) scale(0); }
    }
  `;
  document.head.appendChild(style);
}

// ── Add Button with progress ring ─────────────────────────────────────────

function AddButton() {
  const { isProcessing, progress } = useIngestion();
  // SVG arc for semicircle progress (top half, from left to right)
  const radius = 30;
  const circumference = Math.PI * radius; // half circle
  const offset = circumference - (progress / 100) * circumference;

  return (
    <NavLink
      to="/upload"
      className={({ isActive }) =>
        `flex flex-col items-center gap-0.5 py-2 text-[11px] font-medium transition relative ${
          isActive ? 'text-squishy-tealDark' : 'text-slate-500'
        }`
      }
    >
      <div className="absolute -top-5 w-14 h-14">
        <img src="/logo.png" alt="" className="w-14 h-14 drop-shadow-md" />
        {isProcessing && (
          <svg className="absolute inset-0 w-14 h-14 -rotate-90" viewBox="0 0 64 64">
            <circle
              cx="32" cy="32" r={radius}
              fill="none"
              stroke="#e2e8f0"
              strokeWidth="3"
              strokeDasharray={`${circumference} ${circumference}`}
              strokeDashoffset={0}
              strokeLinecap="round"
              style={{ clipPath: 'inset(0 0 50% 0)' }}
            />
            <circle
              cx="32" cy="32" r={radius}
              fill="none"
              stroke="#0d9488"
              strokeWidth="3"
              strokeDasharray={`${circumference} ${circumference}`}
              strokeDashoffset={offset}
              strokeLinecap="round"
              className="transition-all duration-500"
              style={{ clipPath: 'inset(0 0 50% 0)' }}
            />
          </svg>
        )}
      </div>
      <span className="mt-8">Add</span>
    </NavLink>
  );
}

// ── Ingestion banner (persistent while AI is generating) ─────────────────

function IngestionBanner() {
  const { isProcessing, progress } = useIngestion();
  if (!isProcessing) return null;

  const statusText = progress < 25 ? 'Uploading & parsing...'
    : progress < 50 ? 'Embedding content...'
    : progress < 75 ? 'AI is generating questions...'
    : 'Validating questions...';

  return (
    <div className="bg-gradient-to-r from-teal-500 to-emerald-500 text-white px-4 py-2.5 flex items-center gap-3">
      <svg className="animate-spin w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="60" strokeLinecap="round" className="opacity-30" />
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="60" strokeDashoffset="45" strokeLinecap="round" />
      </svg>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-semibold">{statusText}</div>
        <div className="mt-1 h-1.5 bg-white/30 rounded-full overflow-hidden">
          <div className="h-full bg-white rounded-full transition-all duration-700" style={{ width: `${progress}%` }} />
        </div>
      </div>
      <span className="text-xs font-mono shrink-0">{progress}%</span>
    </div>
  );
}

// ── Completion toast ──────────────────────────────────────────────────────

function CompletionToast() {
  const { completedNotification, clearNotification } = useIngestion();

  useEffect(() => {
    if (!completedNotification) return;
    const timer = setTimeout(clearNotification, 5000);
    return () => clearTimeout(timer);
  }, [completedNotification, clearNotification]);

  if (!completedNotification) return null;

  return (
    <div className="fixed top-16 left-1/2 -translate-x-1/2 z-50 animate-slide-up">
      <div className="bg-white border border-slate-200 shadow-lg rounded-xl px-5 py-3 flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-500/15 flex items-center justify-center">
          <svg className="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
          </svg>
        </div>
        <div>
          <p className="text-sm font-semibold text-slate-800">Questions ready!</p>
          <p className="text-xs text-slate-500">
            {completedNotification.approved} approved of {completedNotification.total} generated
          </p>
        </div>
        <button onClick={clearNotification} className="ml-2 text-slate-400 hover:text-slate-600">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}

function TabLink({ to, icon, label, end }: {
  to: string; icon: React.ReactNode; label: string; end?: boolean;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `flex flex-col items-center gap-0.5 py-2 text-[11px] font-medium transition ${
          isActive ? 'text-squishy-tealDark' : 'text-slate-500'
        }`
      }
    >
      {icon}
      <span>{label}</span>
    </NavLink>
  );
}

// ── Bug Report Button + Modal ────────────────────────────────────────────────

const BUG_CATEGORIES = ['AI Generation', 'Friends', 'Exams', 'Classes', 'Profile', 'Other'] as const;

function BugReportButton() {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const submit = useSubmitBugReport();

  const reset = () => { setCategory(''); setDescription(''); submit.reset(); };

  return (
    <>
      <button
        onClick={() => { reset(); setOpen(true); }}
        className="px-2.5 py-1 rounded-full bg-red-500 text-white text-[10px] font-bold uppercase tracking-wide hover:bg-red-600 transition"
      >
        Bug Report
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div className="relative bg-white rounded-2xl max-w-md w-full shadow-xl animate-slide-up overflow-hidden">
            <div className="bg-red-500 px-5 py-4">
              <h2 className="text-white font-bold text-lg">Report a Bug</h2>
            </div>
            <div className="px-5 py-4 space-y-4">
              {submit.isSuccess ? (
                <div className="text-center py-4">
                  <div className="text-3xl mb-2">&#10003;</div>
                  <p className="font-semibold text-squishy-navy">Thank you!</p>
                  <p className="text-sm text-slate-500 mt-1">Your bug report has been submitted. We'll look into it.</p>
                </div>
              ) : (
                <>
                  <div>
                    <label className="text-sm font-medium text-slate-700 block mb-1">Category</label>
                    <select
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                    >
                      <option value="">Select a category...</option>
                      {BUG_CATEGORIES.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 block mb-1">What's the problem?</label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={4}
                      placeholder="Describe what happened and what you expected..."
                      className="w-full border rounded-lg px-3 py-2 text-sm resize-none"
                    />
                  </div>
                  {submit.isError && (
                    <p className="text-xs text-red-500">Something went wrong. Please try again.</p>
                  )}
                </>
              )}
            </div>
            <div className="px-5 pb-4 flex gap-2">
              {submit.isSuccess ? (
                <button
                  onClick={() => setOpen(false)}
                  className="w-full bg-squishy-tealDark text-white py-2.5 rounded-xl font-semibold text-sm"
                >
                  Done
                </button>
              ) : (
                <>
                  <button
                    onClick={() => setOpen(false)}
                    className="flex-1 border py-2.5 rounded-xl text-sm text-slate-600"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => submit.mutate({ category, description })}
                    disabled={!category || description.trim().length < 5 || submit.isPending}
                    className="flex-1 bg-red-500 text-white py-2.5 rounded-xl font-semibold text-sm disabled:opacity-50"
                  >
                    {submit.isPending ? 'Sending...' : 'Submit'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── Notification Bell ─────────────────────────────────────────────────────

function NotificationBell() {
  const [open, setOpen] = useState(false);
  const { data: countData } = useUnreadNotificationCount();
  const { data: notifData } = useNotifications(10);
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();
  const nav = useRef<typeof import('react-router-dom').useNavigate extends () => infer R ? R : never>(null as any);

  const unread = countData?.count ?? 0;
  const notifications = notifData?.notifications ?? [];

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="relative p-1.5 rounded-full hover:bg-slate-100 transition"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="relative bg-white rounded-2xl max-w-sm w-full shadow-xl animate-slide-up overflow-hidden flex flex-col max-h-[70vh]">
            <div className="flex items-center justify-between px-4 py-3 border-b shrink-0">
              <span className="font-semibold text-sm text-slate-800">Notifications</span>
              <div className="flex items-center gap-3">
                {unread > 0 && (
                  <button
                    onClick={() => markAll.mutate()}
                    className="text-xs text-squishy-primary hover:underline"
                  >
                    Mark all read
                  </button>
                )}
                <button
                  onClick={() => setOpen(false)}
                  aria-label="Close notifications"
                  className="text-slate-400 hover:text-slate-600 transition"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {notifications.length === 0 ? (
                <p className="px-4 py-6 text-center text-sm text-slate-400">No notifications yet</p>
              ) : (
                notifications.map((n: any) => (
                  <button
                    key={n.id}
                    onClick={() => {
                      if (!n.read) markRead.mutate(n.id);
                      setOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 border-b last:border-0 hover:bg-slate-50 transition ${
                      n.read ? 'opacity-60' : ''
                    }`}
                  >
                    <p className="text-sm font-medium text-slate-800">{n.title}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{n.body}</p>
                    <p className="text-[10px] text-slate-400 mt-1">
                      {new Date(n.createdAt).toLocaleDateString()}
                    </p>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
